/**
 * Public booking page (Task 14) — two-pane Calendly-classic layout.
 *
 * Routes (registered OUTSIDE the staff auth gate in App.tsx):
 *   /book/:slug                 — event-type picker (auto-selects if only one)
 *   /book/:slug/:eventTypeSlug  — calendar + slot picker for one event type
 *
 * `?embed=1` strips the navy/gold firm chrome so the page can be iframed
 * on carpentercpas.com. All invitee-visible times render in the invitee's
 * timezone (detected, switchable); the API speaks UTC ISO.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { DateTime } from "luxon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Globe,
  Loader2,
  MapPin,
  Phone,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  getPublicBookingPage,
  getPublicSlots,
  holdSlot,
  confirmHold,
  type PublicBookingPage as PublicBookingPageData,
  type PublicEventType,
  type Slot,
} from "@/lib/bookingApi";

// ---------------------------------------------------------------------------
// Constants & small helpers
// ---------------------------------------------------------------------------

const NAVY = "#0f2747";
const GOLD = "#d9b54a";

const COMMON_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function detectZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

function zoneLabel(zone: string): string {
  return zone.replace(/_/g, " ").replace("America/", "").replace("Europe/", "").replace("Asia/", "").replace("Pacific/", "").replace("Australia/", "");
}

function locationIcon(type: PublicEventType["locationType"]) {
  if (type === "zoom") return <Video className="w-4 h-4 shrink-0" />;
  if (type === "phone") return <Phone className="w-4 h-4 shrink-0" />;
  return <MapPin className="w-4 h-4 shrink-0" />;
}

function locationLabel(et: PublicEventType): string {
  if (et.locationType === "zoom") return "Zoom (link sent on booking)";
  if (et.locationType === "phone") return "Phone call";
  return et.locationDetail ? `In person — ${et.locationDetail}` : "In person";
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

// ---------------------------------------------------------------------------
// Chrome wrapper (firm header in full mode; chrome-less in embed mode)
// ---------------------------------------------------------------------------

function Chrome({ embed, children }: { embed: boolean; children: React.ReactNode }) {
  if (embed) {
    return <div className="w-full font-sans">{children}</div>;
  }
  return (
    <div className="min-h-screen bg-slate-100 font-sans">
      <header
        className="px-5 py-3 text-sm text-white flex items-center gap-2"
        style={{ backgroundColor: NAVY }}
      >
        <span className="font-semibold">Carpenter &amp; Company, CPAs</span>
        <span className="font-bold" style={{ color: GOLD }}>
          | Book a time
        </span>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
}

function MessageCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-white border rounded-xl shadow-sm p-8 max-w-lg mx-auto text-center mt-8">
      <CalendarIcon className="w-10 h-10 mx-auto mb-4 text-slate-300" />
      <h1 className="text-lg font-semibold text-slate-800 mb-2">{title}</h1>
      <p className="text-sm text-slate-500">{body}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden mt-2">
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-[38%] p-6 sm:border-r border-b sm:border-b-0 space-y-3">
          <Skeleton className="w-14 h-14 rounded-full" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex-1 p-6 space-y-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month calendar grid (Sun–Sat) built with luxon — no extra deps
// ---------------------------------------------------------------------------

interface MonthGridProps {
  year: number;
  month: number; // 1–12
  tz: string;
  availableDays: Set<string>; // ISO dates with >= 1 slot
  selectedDay: string | null;
  onSelectDay: (isoDate: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  canGoPrev: boolean;
  loading: boolean;
}

function MonthGrid({
  year,
  month,
  tz,
  availableDays,
  selectedDay,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
  canGoPrev,
  loading,
}: MonthGridProps) {
  const monthStart = DateTime.fromObject({ year, month, day: 1 }, { zone: tz });
  const today = DateTime.now().setZone(tz).startOf("day");
  const leadingBlanks = monthStart.weekday % 7; // luxon: Mon=1..Sun=7 → Sun-first offset
  const daysInMonth = monthStart.daysInMonth ?? 30;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={onPrevMonth}
          disabled={!canGoPrev}
          aria-label="Previous month"
          className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          {monthStart.toFormat("MMMM yyyy")}
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
        <button
          type="button"
          onClick={onNextMonth}
          aria-label="Next month"
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-[11px] font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`b${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = monthStart.plus({ days: i });
          const iso = day.toISODate()!;
          const isPast = day < today;
          const isToday = day.hasSame(today, "day");
          const available = availableDays.has(iso);
          const isSelected = selectedDay === iso;
          const disabled = isPast || !available;
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDay(iso)}
              className={cn(
                "aspect-square rounded-lg text-xs flex items-center justify-center transition-colors",
                isSelected
                  ? "text-white font-bold"
                  : available && !isPast
                    ? "font-semibold bg-blue-50 text-[#0f2747] border border-blue-200 hover:bg-blue-100"
                    : "text-slate-300 cursor-not-allowed",
                isToday && !isSelected && "ring-1 ring-slate-400"
              )}
              style={isSelected ? { backgroundColor: NAVY } : undefined}
            >
              {day.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type ViewState = "pick" | "form" | "success";

interface HoldState {
  holdId: string;
  holdExpiresAt: string;
  slot: Slot;
}

interface SuccessState {
  manageToken: string;
  joinUrl?: string;
  slot: Slot;
}

export default function PublicBookingPage() {
  const [, paramsWithEt] = useRoute("/book/:slug/:eventTypeSlug");
  const [, paramsSlugOnly] = useRoute("/book/:slug");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const slug = paramsWithEt?.slug ?? paramsSlugOnly?.slug ?? "";
  const urlEventTypeSlug = paramsWithEt?.eventTypeSlug ?? null;
  const embed = useMemo(
    () => new URLSearchParams(window.location.search).get("embed") === "1",
    []
  );

  // ---- timezone -----------------------------------------------------------
  const detectedZone = useMemo(detectZone, []);
  const [tz, setTz] = useState(detectedZone);
  const zoneOptions = useMemo(() => {
    const set = new Set([detectedZone, ...COMMON_ZONES]);
    return Array.from(set);
  }, [detectedZone]);

  // ---- page data ----------------------------------------------------------
  const pageQuery = useQuery<PublicBookingPageData, Error>({
    queryKey: ["public-booking-page", slug],
    queryFn: () => getPublicBookingPage(slug),
    enabled: slug.length > 0,
    retry: false,
  });

  const page = pageQuery.data?.page ?? null;
  const eventTypes = pageQuery.data?.eventTypes ?? [];

  // Selected event type: from URL, or auto-select when there is only one.
  const selectedEt: PublicEventType | null = useMemo(() => {
    if (urlEventTypeSlug) return eventTypes.find((et) => et.slug === urlEventTypeSlug) ?? null;
    if (eventTypes.length === 1) return eventTypes[0]!;
    return null;
  }, [urlEventTypeSlug, eventTypes]);

  // ---- booking flow state -------------------------------------------------
  const [view, setView] = useState<ViewState>("pick");
  const [monthCursor, setMonthCursor] = useState<{ year: number; month: number }>(() => {
    const now = DateTime.now();
    return { year: now.year, month: now.month };
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [holding, setHolding] = useState<string | null>(null); // slot.start being held
  const [hold, setHold] = useState<HoldState | null>(null);
  const [holdRemainingSec, setHoldRemainingSec] = useState<number | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Intake form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset flow when the event type changes.
  useEffect(() => {
    setView("pick");
    setHold(null);
    setSelectedDay(null);
    setAnswers({});
    setFormError(null);
  }, [selectedEt?.id]);

  // ---- slots --------------------------------------------------------------
  const monthStart = DateTime.fromObject(
    { year: monthCursor.year, month: monthCursor.month, day: 1 },
    { zone: tz }
  );
  const monthEnd = monthStart.endOf("month");
  const nowUtc = DateTime.utc();
  const fromIso = (monthStart.toUTC() > nowUtc ? monthStart.toUTC() : nowUtc).toISO()!;
  const toIso = monthEnd.toUTC().toISO()!;
  const monthInPast = monthEnd.toUTC() < nowUtc;

  const slotsQueryKey = [
    "public-slots",
    slug,
    selectedEt?.slug ?? "",
    monthCursor.year,
    monthCursor.month,
    tz,
  ];
  const slotsQuery = useQuery<{ slots: Slot[] }, Error>({
    queryKey: slotsQueryKey,
    queryFn: () => getPublicSlots(slug, selectedEt!.slug, fromIso, toIso),
    enabled: !!selectedEt && !monthInPast && pageQuery.isSuccess,
    retry: false,
    staleTime: 30_000,
  });

  const slotsByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const slot of slotsQuery.data?.slots ?? []) {
      const key = DateTime.fromISO(slot.start).setZone(tz).toISODate();
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.start.localeCompare(b.start));
    return map;
  }, [slotsQuery.data, tz]);

  const availableDays = useMemo(() => new Set(slotsByDay.keys()), [slotsByDay]);

  // Default the selected day to the first available day once slots load.
  useEffect(() => {
    if (view !== "pick") return;
    if (selectedDay && availableDays.has(selectedDay)) return;
    const first = Array.from(availableDays).sort()[0] ?? null;
    setSelectedDay(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDays, view]);

  // ---- hold countdown -----------------------------------------------------
  useEffect(() => {
    if (!hold) {
      setHoldRemainingSec(null);
      return;
    }
    const tick = () => {
      const remaining = Math.floor(
        (DateTime.fromISO(hold.holdExpiresAt).toMillis() - Date.now()) / 1000
      );
      setHoldRemainingSec(Math.max(0, remaining));
      if (remaining <= 0) {
        setHold(null);
        setView("pick");
        toast({
          title: "Hold expired",
          description: "Your time hold expired — please pick a time again.",
          variant: "destructive",
        });
        queryClient.invalidateQueries({ queryKey: ["public-slots"] });
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold]);

  // ---- actions ------------------------------------------------------------
  const pickEventType = (et: PublicEventType) => {
    setLocation(`/book/${slug}/${et.slug}${embed ? "?embed=1" : ""}`);
  };

  const backToTypes = () => {
    setLocation(`/book/${slug}${embed ? "?embed=1" : ""}`);
  };

  const onSlotClick = async (slot: Slot) => {
    if (!selectedEt || holding) return;
    setHolding(slot.start);
    try {
      const result = await holdSlot(slug, selectedEt.slug, slot.start, slot.end, tz);
      if (!result.ok) {
        toast({
          title: "That time was just taken",
          description: "Someone else grabbed that slot. Please pick another time.",
          variant: "destructive",
        });
        await slotsQuery.refetch();
        return;
      }
      setHold({ holdId: result.holdId, holdExpiresAt: result.holdExpiresAt, slot });
      setFormError(null);
      setView("form");
    } catch (err) {
      toast({
        title: "Couldn't hold that time",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setHolding(null);
    }
  };

  const onConfirm = async () => {
    if (!hold || !selectedEt || confirming) return;
    if (!name.trim()) {
      setFormError("Please enter your name.");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setFormError("Please enter a valid email address.");
      return;
    }
    for (const q of selectedEt.questions) {
      if (q.required && !(answers[q.label] ?? "").trim()) {
        setFormError(`"${q.label}" is required.`);
        return;
      }
    }
    setFormError(null);
    setConfirming(true);
    try {
      const result = await confirmHold(hold.holdId, {
        name: name.trim(),
        email: email.trim(),
        timezone: tz,
        answers,
      });
      if (!result.ok) {
        toast({
          title: "Hold expired",
          description: "Your time hold expired — please pick a time again.",
          variant: "destructive",
        });
        setHold(null);
        setView("pick");
        await slotsQuery.refetch();
        return;
      }
      setSuccess({
        manageToken: result.manageToken,
        ...(result.joinUrl ? { joinUrl: result.joinUrl } : {}),
        slot: hold.slot,
      });
      setHold(null);
      setView("success");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Booking failed — please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const manageUrl = success
    ? `${window.location.origin}/booking/${success.manageToken}`
    : "";

  const copyManageLink = async () => {
    try {
      await navigator.clipboard.writeText(manageUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: manageUrl });
    }
  };

  // ---- render: load / error states ----------------------------------------
  if (!slug) {
    return (
      <Chrome embed={embed}>
        <MessageCard
          title="This booking page doesn't exist or is inactive."
          body="Double-check the link you were given, or contact the firm directly."
        />
      </Chrome>
    );
  }

  if (pageQuery.isLoading) {
    return (
      <Chrome embed={embed}>
        <LoadingSkeleton />
      </Chrome>
    );
  }

  if (pageQuery.isError || !pageQuery.data) {
    const notFound = pageQuery.error?.message === "Not found";
    return (
      <Chrome embed={embed}>
        {notFound ? (
          <MessageCard
            title="This booking page doesn't exist or is inactive."
            body="Double-check the link you were given, or contact the firm directly."
          />
        ) : (
          <MessageCard
            title="Scheduling is temporarily unavailable"
            body="We couldn't load this booking page right now. Please try again in a few minutes, or contact the firm directly to schedule."
          />
        )}
      </Chrome>
    );
  }

  // ---- render: main two-pane layout ---------------------------------------
  const daySlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : [];
  const nowMonth = DateTime.now().setZone(tz);
  const canGoPrev =
    monthCursor.year > nowMonth.year ||
    (monthCursor.year === nowMonth.year && monthCursor.month > nowMonth.month);

  return (
    <Chrome embed={embed}>
      <div
        className={cn(
          "bg-white border rounded-xl shadow-sm overflow-hidden",
          embed ? "" : "mt-2"
        )}
      >
        <div className="flex flex-col sm:flex-row">
          {/* ============== LEFT PANE — context ============== */}
          <div className="sm:w-[38%] p-6 border-b sm:border-b-0 sm:border-r bg-slate-50/50">
            {urlEventTypeSlug && eventTypes.length > 1 && view !== "success" && (
              <button
                type="button"
                onClick={backToTypes}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-4"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> All meeting types
              </button>
            )}

            {page!.photoUrl ? (
              <img
                src={page!.photoUrl}
                alt={page!.displayName}
                className="w-14 h-14 rounded-full object-cover mb-3"
              />
            ) : (
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg mb-3"
                style={{ backgroundColor: NAVY, color: GOLD }}
              >
                {initials(page!.displayName)}
              </div>
            )}
            <div className="font-semibold text-slate-900">{page!.displayName}</div>
            {page!.title && <div className="text-sm text-slate-500">{page!.title}</div>}

            {selectedEt && (
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                <div className="font-semibold text-slate-800">{selectedEt.name}</div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 shrink-0" />
                  {selectedEt.durationMin} minutes
                </div>
                <div className="flex items-center gap-2">
                  {locationIcon(selectedEt.locationType)}
                  {locationLabel(selectedEt)}
                </div>
                {selectedEt.description && (
                  <p className="text-xs text-slate-500 pt-1 whitespace-pre-line">
                    {selectedEt.description}
                  </p>
                )}
              </div>
            )}

            {/* Timezone picker */}
            <div className="mt-6">
              <Label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1.5">
                <Globe className="w-3.5 h-3.5" /> Timezone
              </Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger className="h-8 text-xs bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {zoneOptions.map((z) => (
                    <SelectItem key={z} value={z} className="text-xs">
                      {zoneLabel(z)}
                      {z === detectedZone ? " (detected)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ============== RIGHT PANE ============== */}
          <div className="flex-1 p-6">
            {/* --- no event type selected → picker list --- */}
            {!selectedEt && (
              <div>
                <h2 className="text-sm font-semibold text-slate-800 mb-3">
                  Select a meeting type
                </h2>
                {eventTypes.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No meeting types are currently available on this page.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {eventTypes.map((et) => (
                      <button
                        key={et.id}
                        type="button"
                        onClick={() => pickEventType(et)}
                        className="w-full text-left border rounded-lg px-4 py-3 hover:border-[#0f2747] hover:bg-blue-50/40 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-slate-800">{et.name}</span>
                          <span className="text-xs text-slate-500 flex items-center gap-1.5 shrink-0">
                            <Clock className="w-3.5 h-3.5" /> {et.durationMin} min
                            <span className="mx-0.5">·</span>
                            {locationIcon(et.locationType)}
                          </span>
                        </div>
                        {et.description && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                            {et.description}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* --- calendar + slots --- */}
            {selectedEt && view === "pick" && (
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 min-w-0">
                  <MonthGrid
                    year={monthCursor.year}
                    month={monthCursor.month}
                    tz={tz}
                    availableDays={availableDays}
                    selectedDay={selectedDay}
                    onSelectDay={setSelectedDay}
                    onPrevMonth={() =>
                      setMonthCursor((c) =>
                        c.month === 1
                          ? { year: c.year - 1, month: 12 }
                          : { year: c.year, month: c.month - 1 }
                      )
                    }
                    onNextMonth={() =>
                      setMonthCursor((c) =>
                        c.month === 12
                          ? { year: c.year + 1, month: 1 }
                          : { year: c.year, month: c.month + 1 }
                      )
                    }
                    canGoPrev={canGoPrev}
                    loading={slotsQuery.isFetching}
                  />
                  {slotsQuery.isError && (
                    <p className="text-xs text-rose-600 mt-3">
                      Couldn't load available times — please try again shortly.
                    </p>
                  )}
                  {!slotsQuery.isFetching && !slotsQuery.isError && availableDays.size === 0 && (
                    <p className="text-xs text-slate-500 mt-3">
                      No times available this month — try the next month.
                    </p>
                  )}
                </div>
                <div className="md:w-40 shrink-0">
                  {selectedDay && (
                    <>
                      <div className="text-xs font-semibold text-slate-700 mb-2">
                        {DateTime.fromISO(selectedDay, { zone: tz }).toFormat("ccc, MMM d")}
                      </div>
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {daySlots.map((slot) => {
                          const t = DateTime.fromISO(slot.start).setZone(tz);
                          const isHolding = holding === slot.start;
                          return (
                            <button
                              key={slot.start}
                              type="button"
                              disabled={!!holding}
                              onClick={() => onSlotClick(slot)}
                              className="w-full border border-blue-200 rounded-lg py-2 text-xs font-semibold text-[#0f2747] bg-white hover:bg-blue-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                            >
                              {isHolding && <Loader2 className="w-3 h-3 animate-spin" />}
                              {t.toFormat("h:mm a")}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* --- intake form --- */}
            {selectedEt && view === "form" && hold && (
              <div className="max-w-sm">
                <button
                  type="button"
                  onClick={() => {
                    setHold(null);
                    setView("pick");
                  }}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Pick a different time
                </button>
                <h2 className="text-sm font-semibold text-slate-800">
                  {DateTime.fromISO(hold.slot.start).setZone(tz).toFormat("cccc, MMMM d 'at' h:mm a")}
                </h2>
                {holdRemainingSec !== null && (
                  <p className="text-xs text-amber-600 mt-1">
                    Time held for{" "}
                    <span className="font-semibold tabular-nums">
                      {Math.floor(holdRemainingSec / 60)}:
                      {String(holdRemainingSec % 60).padStart(2, "0")}
                    </span>
                  </p>
                )}

                <div className="space-y-3 mt-4">
                  <div>
                    <Label htmlFor="bk-name" className="text-xs">
                      Name <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="bk-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 h-9"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bk-email" className="text-xs">
                      Email <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="bk-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 h-9"
                      autoComplete="email"
                    />
                  </div>

                  {selectedEt.questions.map((q) => (
                    <div key={q.label}>
                      <Label htmlFor={`bk-q-${q.label}`} className="text-xs">
                        {q.label} {q.required && <span className="text-rose-500">*</span>}
                      </Label>
                      {q.fieldType === "textarea" ? (
                        <Textarea
                          id={`bk-q-${q.label}`}
                          value={answers[q.label] ?? ""}
                          onChange={(e) =>
                            setAnswers((a) => ({ ...a, [q.label]: e.target.value }))
                          }
                          className="mt-1"
                          rows={3}
                        />
                      ) : q.fieldType === "select" ? (
                        <Select
                          value={answers[q.label] || undefined}
                          onValueChange={(v) => setAnswers((a) => ({ ...a, [q.label]: v }))}
                        >
                          <SelectTrigger id={`bk-q-${q.label}`} className="mt-1 h-9">
                            <SelectValue placeholder="Choose…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(q.options ?? []).map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={`bk-q-${q.label}`}
                          type={q.fieldType === "phone" ? "tel" : "text"}
                          value={answers[q.label] ?? ""}
                          onChange={(e) =>
                            setAnswers((a) => ({ ...a, [q.label]: e.target.value }))
                          }
                          className="mt-1 h-9"
                        />
                      )}
                    </div>
                  ))}

                  {formError && <p className="text-xs text-rose-600">{formError}</p>}

                  <Button
                    onClick={onConfirm}
                    disabled={confirming}
                    className="w-full text-white"
                    style={{ backgroundColor: NAVY }}
                  >
                    {confirming && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Confirm booking
                  </Button>
                </div>
              </div>
            )}

            {/* --- success --- */}
            {selectedEt && view === "success" && success && (
              <div className="max-w-md">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h2 className="text-base font-semibold text-slate-900">You're booked!</h2>
                </div>
                <div className="border rounded-lg p-4 space-y-2 text-sm text-slate-700">
                  <div className="font-semibold text-slate-900">{selectedEt.name}</div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 shrink-0" />
                    {DateTime.fromISO(success.slot.start)
                      .setZone(tz)
                      .toFormat("cccc, MMMM d, yyyy 'at' h:mm a")}{" "}
                    ({zoneLabel(tz)})
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 shrink-0" />
                    {selectedEt.durationMin} minutes
                  </div>
                  <div className="flex items-start gap-2">
                    {locationIcon(selectedEt.locationType)}
                    {selectedEt.locationType === "zoom" ? (
                      success.joinUrl ? (
                        <a
                          href={success.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 underline break-all"
                        >
                          Join Zoom meeting
                        </a>
                      ) : (
                        <span>Zoom — your join link is in the confirmation email.</span>
                      )
                    ) : selectedEt.locationType === "phone" ? (
                      <span>Phone call — details are in your confirmation email.</span>
                    ) : (
                      <span>{locationLabel(selectedEt)}</span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  A confirmation email with a calendar invite is on its way to{" "}
                  <span className="font-medium">{email.trim()}</span>.
                </p>
                <div className="mt-4">
                  <Label className="text-xs text-slate-500">
                    Need to reschedule or cancel? Use this link:
                  </Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input readOnly value={manageUrl} className="h-8 text-xs" />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0"
                      onClick={copyManageLink}
                    >
                      {copied ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {!embed && (
          <div className="text-center text-[11px] text-slate-400 py-2.5 border-t bg-slate-50/60">
            Times are shown in <b>your</b> timezone · availability reflects live calendars
          </div>
        )}
      </div>
    </Chrome>
  );
}
